import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * tailwind-merge can't see the CSS build, so it can't know our custom
 * `--text-*` @theme tokens (globals.css) are font SIZES — Tailwind overloads
 * `text-*` for both size and color, and unknown values default to color.
 * Without this, `cn('text-body-sm', 'text-foreground/90')` silently deletes
 * the size token as a "conflicting color".
 *
 * Official recipe: https://github.com/dcastil/tailwind-merge/blob/main/docs/configuration.md
 * RULE: every new `--text-*` token added to globals.css MUST be added here.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: [
        'label',
        'caption',
        'body',
        'body-sm',
        'body-lg',
        'title',
        'card-title',
        'value',
        'value-sm',
        'value-lg',
        'micro',
        'h1',
        'hero',
        'page-title',
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Shared initials fallback for avatars — up to 2 letters from `name`, falling
 * back to the first letter of `email`, then `?`. Used wherever an avatar has no
 * image (AuthorAvatar, etc).
 */
export function getInitials(name: string, email?: string | null): string {
  const trimmed = name.trim()
  if (trimmed.length > 0) {
    return trimmed
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0] ?? '')
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }
  return (email?.[0] ?? '?').toUpperCase()
}

/**
 * Formats a microdollar amount (1 USD = 1,000,000 µ$, see `MODEL_COSTS` in
 * `src/lib/ai/cost-tracker.ts`) as a compact USD string, widening decimal
 * precision for very small amounts so sub-cent AI costs stay legible instead
 * of rounding to "$0.00". Used by admin cost-tracking UI.
 */
export function formatCost(microdollars: number): string {
  const d = microdollars / 1_000_000
  if (d === 0) return '$0.00'
  if (d < 0.0001) return '<$0.0001'
  if (d < 0.001) return `$${d.toFixed(5)}`
  if (d < 0.01) return `$${d.toFixed(4)}`
  if (d < 1) return `$${d.toFixed(3)}`
  return `$${d.toFixed(2)}`
}
